<template>
  <div class="qr-dialog-content">
    <!-- Minimal layout -->
    <template v-if="layout === 'minimal'">
      <v-card class="p-3">
        <div class="d-flex">
          <font-awesome-icon
            @click="onClose"
            icon="times"
            class="ml-auto f-title"
            data-test="modal-qr-minimal-close-icon"
          ></font-awesome-icon>
        </div>
        <div class="f-bold mb-6">
          {{ $t("coupon_note_title") || "REDEEM VOUCHER" }}
        </div>
        <div class="coupon-container">
          <div class="text-center">
            <qrcode-vue
              :value="
                $store.state.theme.qr_code == 'Telephone'
                  ? profile.telephone
                  : profile.member_id
              "
              :size="160"
              class="mt-7"
              render-as="svg"
              background="transparent"
            />
            <div>
              <span
                data-lang="telephone"
                v-if="$store.state.theme.qr_code == 'Telephone'"
                >{{ $t("telephone") }}
              </span>
              <span data-lang="member_id" v-else>{{ $t("member_id") }} </span>
              :
              {{
                $store.state.theme.qr_code == "Telephone"
                  ? profile.telephone
                  : profile.member_id
              }}
            </div>
          </div>
          <div class="voucher-code">
            <div style="margin-left: 7px">
              {{ $t("voucher_active") || "Voucher Active" }}
            </div>
            <Barcode :value="code" />
            <div style="margin-left: 7px">
              <span data-lang="coupon_code">{{ $t("coupon_code") }}</span> :
              {{ code ? code : "-" }}
            </div>
          </div>
          <div class="countdown-time mt-2" v-if="markUse">
            <div data-lang="qr_count_time" class="text-center">
              {{ $t("qr_count_time") }}&nbsp;{{ countTime }}
            </div>
          </div>
          <div class="coupon-remark">
            {{
              $t("coupon_remark") ||
              "You cannot redeem another voucher while this one is still active"
            }}
          </div>
          <div>
            <c-btn
              @click="onClose"
              variant="outline-primary"
              block
              data-test="modal-qr-minimal-close-btn"
            >
              <span data-lang="close_window">{{ $t("back") }}</span>
            </c-btn>
          </div>
        </div>
      </v-card>
    </template>

    <!-- Standard V2 layout -->
    <template v-else-if="layout === 'standard-v2' || isStandardV2">
      <div class="modal-qr-v2__content">
        <div class="modal-qr-v2__header">
          <h2>{{ name }}</h2>
        </div>

        <div class="modal-qr-v2__ticket">
          <div class="modal-qr-v2__tabs" data-test="modal-qr-tabs">
            <button
              type="button"
              :class="[
                'modal-qr-v2__tab',
                { 'is-active': activeDisplayMode === 'qr' }
              ]"
              data-test="modal-qr-qrcode-tab"
              @click="activeDisplayMode = 'qr'"
            >
              <!-- <font-awesome-icon icon="qrcode" /> -->
              {{ $t("qr_code") }}
            </button>
            <button
              type="button"
              :class="[
                'modal-qr-v2__tab',
                { 'is-active': activeDisplayMode === 'barcode' }
              ]"
              data-test="modal-qr-barcode-tab"
              @click="activeDisplayMode = 'barcode'"
            >
              <!-- <font-awesome-icon icon="barcode" /> -->
              {{ $t("barcode") }}
            </button>
          </div>

          <div
            v-if="activeDisplayMode === 'qr'"
            class="modal-qr-v2__code-box"
            data-test="modal-qr-code"
          >
            <qrcode-vue
              class="modal-qr-v2__qr"
              :value="code"
              render-as="svg"
              background="transparent"
            />
          </div>
          <div
            v-else
            class="modal-qr-v2__code-box"
            data-test="modal-qr-barcode"
          >
            <Barcode :value="displayCode" />
          </div>
          <span class="text-center f-body" data-lang="coupon_code">{{
            $t("coupon_code")
          }}</span>
          <button
            type="button"
            class="modal-qr-v2__product-code"
            :disabled="!code"
            data-test="modal-qr-copy-btn"
            @click="copyCode"
          >
            <strong class="color-primary">{{ displayCode }}</strong>
            <span
              class="copy-icon"
              :style="{ '--icon-url': `url(${$baseUrl}svg/copy.svg)` }"
              aria-hidden="true"
            ></span>
          </button>

          <div
            v-if="markUse"
            class="modal-qr-v2__timer"
            data-test="modal-qr-countdown"
          >
            <span data-lang="qr_count_time">{{ $t("qr_count_time") }}</span>
            <strong>{{ countTime }} </strong>
          </div>
        </div>

        <div class="modal-qr-v2__note" data-test="modal-qr-note">
          <div v-html="$t('coupon_note', { minute: '10' })"></div>
        </div>

        <div class="modal-qr-v2__actions">
          <c-btn
            v-if="allow"
            type="button"
            class="modal-qr-v2__primary-btn"
            data-test="modal-qr-confirm-btn"
            @click="onUse"
          >
            <span data-lang="confirm_used">{{ $t("confirm_used") }}</span>
          </c-btn>
          <c-btn
            type="button"
            variant="outline-primary"
            data-test="modal-qr-close-btn"
            @click="onClose"
          >
            <span data-lang="close_window">{{ $t("close_window") }}</span>
          </c-btn>
        </div>
      </div>
    </template>

    <!-- Standard layout (default) -->
    <template v-else>
      <div>
        <div
          class="mx-3 mt-3 mb-2 text-center px-3 bg-white header-qr line-clamp-2"
        >
          {{ name }}
        </div>
      </div>
      <div class="position-relative" style="height: 45px">
        <div class="dot dot-left" style="left: 0; z-index: 1"></div>
        <div class="border-behind"></div>
        <div class="dot dot-right" style="right: 0"></div>
      </div>
      <div class="text-center px-3 pb-3">
        <div class="f-bold f-size-18 d-flex align-center justify-center gap-2">
          <span>
            <span data-lang="coupon_code" data-test="coupon_code">{{
              $t("coupon_code")
            }}</span>
            :
            {{ code ? code : "-" }}
          </span>
          <font-awesome-icon
            v-if="code"
            :icon="copied ? 'check' : 'copy'"
            class="copy-btn"
            data-test="modal-qr-copy-btn"
            @click="copyCode"
          />
        </div>

        <div>
          <qrcode-vue
            class="qr-width-70"
            :value="code"
            render-as="svg"
            background="transparent"
          />
        </div>

        <small data-lang="coupon_note" class="error" v-html="$t('coupon_note')"> </small>
        <div class="timer-countdown mt-2" v-if="markUse">
          <div data-lang="qr_count_time" data-test="qr_count_time">
            {{ $t("qr_count_time") }}
          </div>
          <div>
            {{ countTime }}
          </div>
        </div>
      </div>
      <hr />
      <div v-if="allow">
        <c-btn
          @click="onUse"
          variant="confirm-primary"
          block
          data-test="modal-qr-confirm-btn"
        >
          <span data-lang="confirm_used" data-test="confirm_used">{{
            $t("confirm_used")
          }}</span>
        </c-btn>
      </div>
      <div>
        <c-btn
          @click="onClose"
          variant="outline-primary"
          block
          data-test="modal-qr-close-btn"
        >
          <span data-lang="close_window" data-test="close_window">{{
            $t("close_window")
          }}</span>
        </c-btn>
      </div>
    </template>
  </div>
</template>

<script>
import Barcode from "@/components/other/Barcode.vue";
let countdownInterval;
export default {
  name: "QRDialogContent",
  components: {
    Barcode
  },
  props: {
    code: { type: String, required: true, default: "" },
    name: { type: String, default: "" },
    allow: { default: false },
    countTime: { default: "00:00" },
    markUse: { type: Boolean, default: true },
    layout: { type: String, default: "standard" },
    profile: { type: Object, default: () => ({}) },
    isBottomSheet: { type: Boolean, default: false }
  },
  emits: ["action", "close", "use"],
  data() {
    return {
      activeDisplayMode: "qr",
      copied: false
    };
  },
  computed: {
    isStandardV2() {
      return this.$isStandardV2 || this.lineLayout === "standard-v2";
    },
    displayCode() {
      return this.code || "-";
    }
  },
  beforeUnmount() {
    clearInterval(countdownInterval);
  },
  methods: {
    onUse() {
      this.$emit("use");
      this.$emit("action", { action: "use" });
    },
    onClose() {
      clearInterval(countdownInterval);
      this.$emit("close");
    },
    async copyCode() {
      if (!this.code) return;
      try {
        await navigator.clipboard.writeText(this.code);
      } catch {
        const el = document.createElement("textarea");
        el.value = this.code;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      this.copied = true;
      this.emitter.emit("showToast", {
        message: this.$t("copy_success") || "คัดลอกสำเร็จ",
        type: "success",
        duration: 3000
      });
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
    countTimer() {
      function padZero(value) {
        value = String(value);
        return value.length == 1 ? "0" + value : value;
      }
      var diff = this.$moment().add(30, "minutes").valueOf();
      if (this.$cookies.isKey("coupon")) {
        let cookie = this.$cookies.get("coupon");
        let cache = cookie.findIndex(
          el => el.id == this.$route.params.privilegeId
        );
        if (cache != -1) {
          diff = cookie[cache].end;
        } else {
          this.$cookies.set(
            "coupon",
            cookie.push({
              id: this.$route.params.privilegeId,
              start: this.$moment(),
              end: this.$moment().add(30, "minutes").valueOf()
            })
          );
        }
      } else {
        this.$cookies.set(
          "coupon",
          JSON.stringify([
            {
              id: this.$route.params.privilegeId,
              start: this.$moment(),
              end: this.$moment().add(30, "minutes").valueOf()
            }
          ])
        );
      }
      var countDownDate = diff;
      countdownInterval = setInterval(() => {
        var now = this.$moment().valueOf();
        var distance = countDownDate - now;
        var minutes = padZero(
          Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
        );
        var seconds = padZero(Math.floor((distance % (1000 * 60)) / 1000));
        if (distance < 0) {
          return clearInterval(countdownInterval);
        }
        this.$nextTick(() => {
          this.$emit("action", {
            action: "tick",
            countTime: minutes + " : " + seconds
          });
        });
      }, 1000);
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-body {
  margin: auto;
  text-align: center;
}
.image-logo {
  width: 100%;
  height: 100%;
}
.btn-outline {
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}
.dot {
  display: inline-block;
  top: 6%;
  position: absolute;
  width: 20px;
  height: 45px;
  border: 10px solid #000;
  border-left: 0;
  background-color: #000;
}
.error {
  color: red;
}
.dot-left {
  left: 0;
  border-bottom-right-radius: 100px;
  border-top-right-radius: 100px;
}
.dot-right {
  border-bottom-left-radius: 100px;
  border-top-left-radius: 100px;
  right: 0;
}
.border-behind {
  border-top: 4px dashed #dfdfdf;
  position: absolute;
  top: 50%;
  width: 100%;
  left: 0;
}
.line-clamp-2 {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
}
.qr-width-70 {
  width: 80% !important;
  height: 80% !important;
  padding: 0.5rem 0;
}
.copy-btn {
  cursor: pointer;
  color: var(--primary-color);
  transition: color 0.2s;
  &:hover {
    opacity: 0.7;
  }
}
.voucher-code {
  width: 75%;
  margin: 0 auto;
  margin-top: auto;
  margin-bottom: auto;
}
.coupon-remark {
  width: 75%;
  text-align: center;
  margin: auto auto 15px auto;
}
.coupon-container {
  display: flex;
  justify-content: space-around;
  flex-direction: column;
  height: 100%;
}
.countdown-time {
  font-weight: 600;
  margin-bottom: 1rem;
  width: 75%;
  margin: auto auto 15px auto;
}
</style>
